/**
 * Cloisonnement du panier pendant une vente Friends & Family (F&F).
 *
 * Règles (inertes tant que settings.ff_tag n'est pas renseigné) :
 *   - panier contenant du F&F + ajout d'un produit non taggué => ajout refusé ;
 *   - ajout d'un produit F&F dans un panier contenant des articles non taggués
 *     => les articles non taggués sont retirés, puis l'ajout se poursuit ;
 *   - un panier mixte détecté a posteriori (course entre deux onglets, panier
 *     antérieur à l'activation, permalien) est réconcilié automatiquement :
 *     le F&F gagne, les articles non taggués sont retirés ;
 *   - au clic checkout, l'état est revalidé auprès du serveur (l'état DOM d'un
 *     onglet peut être périmé) : panier mixte => réconciliation et on reste sur
 *     le panier, pour que le client voie ce qu'il va payer ;
 *   - un panier contenant du F&F porte le cart attribute `_ff_sale` (valeur =
 *     tag de la vente), qui suit la commande en note_attribute : c'est ce qui
 *     permet d'identifier a posteriori les commandes F&F (admin, API, Flow).
 *
 * Interception : un listener 'submit' en phase capture sur document passe avant
 * celui que <product-form> pose sur son propre <form>, donc stopPropagation()
 * suffit à annuler l'ajout sans toucher à product-form.js. Les trois formulaires
 * d'ajout du thème (buy-buttons, cart_upsell_product, complete_the_look) passent
 * tous par là, et aucun autre code ne tape /cart/add.js. Les deux boutons
 * checkout (drawer et page panier) sont des [type=submit][name=checkout] liés
 * par attribut form= : le même listener les couvre via event.submitter.
 *
 * État du panier : lu dans #FFCartState, rendu par snippets/ff-cart-state.liquid
 * dans #cart-icon-bubble — la seule section re-rendue par tous les chemins de
 * mise à jour du panier. Pas de cache à maintenir ici, donc pas de désync dans
 * l'onglet courant ; pour les autres onglets, voir la resynchronisation au
 * refocus et la revalidation au checkout.
 */
(function () {
  const STATE_ELEMENT_ID = 'FFCartState';
  const BYPASS_FLAG = 'ffGuardBypass';
  const NOTICE_CLASS = 'ff-cart-guard__notice';
  // Cart attribute posé sur les paniers F&F ; devient un note_attribute de la
  // commande (admin > commande > « Détails supplémentaires », API, Flow).
  const ORDER_ATTRIBUTE = '_ff_sale';

  const DEFAULT_BLOCKED_MESSAGE =
    'Your cart is reserved for Friends & Family items. Please complete this order before adding other products.';
  const DEFAULT_PURGED_MESSAGE =
    'Your cart now contains Friends & Family items only. The other items have been removed.';

  // Une seule purge à la fois : évite qu'une réconciliation déclenchée par
  // cartUpdate en chevauche une autre lancée par le gate checkout.
  let purgeInFlight = false;

  /* ------------------------------------------------------------------ état */

  function readState() {
    const element = document.getElementById(STATE_ELEMENT_ID);
    if (!element) return null;

    try {
      const state = JSON.parse(element.textContent);
      if (!state || !state.tag) return null;
      return {
        tag: state.tag,
        ff: Array.isArray(state.ff) ? state.ff : [],
        std: Array.isArray(state.std) ? state.std : [],
        attr: typeof state.attr === 'string' ? state.attr : '',
        blockedMessage: state.blockedMessage || DEFAULT_BLOCKED_MESSAGE,
        purgedMessage: state.purgedMessage || DEFAULT_PURGED_MESSAGE,
      };
    } catch (error) {
      console.error('ff-cart-guard : état du panier illisible', error);
      return null;
    }
  }

  function isMixed(state) {
    return state !== null && state.ff.length > 0 && state.std.length > 0;
  }

  /**
   * Rafraîchit #cart-icon-bubble depuis le serveur puis relit l'état à jour.
   * Resynchronise au passage le compteur panier de l'onglet courant (utile
   * quand le panier a été modifié depuis un autre onglet).
   */
  function fetchFreshState() {
    return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => {
        if (!response.ok) throw new Error(`section cart-icon-bubble a répondu ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('.shopify-section');
        const bubble = document.getElementById('cart-icon-bubble');
        if (source && bubble) bubble.innerHTML = source.innerHTML;
        return readState();
      });
  }

  /* -------------------------------------------------------------- messages */

  function isFFProduct(form) {
    const host = form.closest('product-form');
    return host !== null && host.dataset.ff === 'true';
  }

  function setLoading(form, isLoading) {
    const button = form.querySelector('[type="submit"]');
    const spinner = form.querySelector('.loading__spinner');
    if (button) button.classList.toggle('loading', isLoading);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  /**
   * Réutilise le bloc d'erreur de Dawn quand il existe (PDP) et le crée sinon
   * (upsell, complete the look) : product-form.js le retrouvera et le masquera
   * de lui-même au prochain ajout, via handleErrorMessage().
   */
  function showFormMessage(form, text) {
    const host = form.closest('product-form') || form;
    let wrapper = host.querySelector('.product-form__error-message-wrapper');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');

      const span = document.createElement('span');
      span.className = 'product-form__error-message';
      wrapper.appendChild(span);
      host.prepend(wrapper);
    }

    const message = wrapper.querySelector('.product-form__error-message');
    // innerHTML : les messages viennent des fichiers de locale (clés *_html,
    // contenu marchand), pas d'une saisie client — le HTML y est voulu (<br>…).
    if (message) message.innerHTML = text;
    wrapper.removeAttribute('hidden');
  }

  /**
   * Message posté en tête du cart-drawer après une purge. Inséré en frère de
   * .drawer__header, donc en dehors de <cart-drawer-items> que cart.js remplace
   * de son côté : il survit aux re-render du drawer. Retiré à la mise à jour
   * suivante du panier, où il n'a plus de sens.
   */
  function showDrawerNotice(text) {
    const header = document.querySelector('#CartDrawer .drawer__header');
    if (!header) return;

    document.querySelectorAll('.' + NOTICE_CLASS).forEach((node) => node.remove());

    const notice = document.createElement('p');
    notice.className = 'form__message ' + NOTICE_CLASS;
    notice.setAttribute('role', 'status');
    // innerHTML : même logique que showFormMessage — contenu marchand (*_html).
    notice.innerHTML = text;
    header.insertAdjacentElement('afterend', notice);

    if (typeof subscribe !== 'function') return;
    const unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      unsubscribe();
      notice.remove();
    });
  }

  /**
   * Le message n'a de sens qu'une fois le drawer re-rendu par l'ajout qui suit la
   * purge. On écoute donc l'issue de cet ajout — cartUpdate en cas de succès,
   * cartError s'il échoue (taille devenue indisponible, par exemple) — et on se
   * désabonne des deux dans les deux cas, pour ne pas afficher le message plus
   * tard, à contretemps.
   */
  function noticeOnNextUpdate(text) {
    if (typeof subscribe !== 'function') return;

    let unsubscribers = [];

    function settle(onSettled) {
      return function () {
        unsubscribers.forEach((unsubscribe) => unsubscribe());
        unsubscribers = [];
        onSettled();
      };
    }

    unsubscribers.push(
      subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        settle(() => {
          // L'événement est publié juste avant le re-render du drawer : on attend
          // la fin de la tâche courante pour écrire dans le markup définitif.
          setTimeout(() => showDrawerNotice(text));
        })
      )
    );
    unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, settle(() => {})));
  }

  /* ----------------------------------------------------------------- purge */

  function removeLines(keys, sections) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const body = { updates: updates };
    if (sections && sections.length > 0) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`cart/update.js a répondu ${response.status}`);
      return response.json();
    });
  }

  // Sections à re-rendre quand la purge n'est pas suivie d'un ajout (qui, lui,
  // re-rend tout via product-form.js).
  function sectionsToRefresh() {
    const sections = ['cart-icon-bubble'];
    if (document.getElementById('CartDrawer')) sections.push('cart-drawer');

    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) sections.push(mainItems.dataset.id);

    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) sections.push(mainFooter.dataset.id);

    return sections;
  }

  function renderRefreshedSections(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    // Tout .drawer__inner, comme le fait cart.js pour un changement de quantité :
    // le bouton checkout du drawer (dans .drawer__footer) porte le total, il doit
    // être rafraîchi lui aussi.
    const drawer = document.getElementById('CartDrawer');
    if (drawer && sections['cart-drawer']) {
      const target = drawer.querySelector('.drawer__inner');
      const source = parse(sections['cart-drawer']).querySelector('.drawer__inner');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id && sections[mainItems.dataset.id]) {
      const target = mainItems.querySelector('.js-contents');
      const source = parse(sections[mainItems.dataset.id]).querySelector('.js-contents');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    // Le footer entier, pas seulement son .js-contents : le bouton checkout de la
    // page panier porte le total lui aussi.
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id && sections[mainFooter.dataset.id]) {
      const source = parse(sections[mainFooter.dataset.id]).getElementById('main-cart-footer');
      if (source) mainFooter.innerHTML = source.innerHTML;
    }
  }

  /**
   * Retire les lignes hors F&F d'un panier mixte, re-rend les sections
   * concernées, puis publie cartUpdate avec source 'cart-items' : cart.js
   * ignore cette source dans onCartUpdate (le rendu est déjà fait ici) tandis
   * que tracking-cart.js y déclenche ses événements remove_from_cart par diff.
   * Résout à true si la purge a bien eu lieu.
   */
  function purgeStandardLines(state) {
    if (purgeInFlight) return Promise.resolve(false);
    purgeInFlight = true;

    return removeLines(state.std, sectionsToRefresh())
      .then((cart) => {
        renderRefreshedSections(cart.sections);
        if (typeof publish === 'function') {
          return Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart })).then(
            () => true
          );
        }
        return true;
      })
      .finally(() => {
        purgeInFlight = false;
      });
  }

  /* ------------------------------------------------- attribut de commande */

  let attrSyncInFlight = false;
  // Dernière valeur effectivement posée : l'état DOM peut être en retard d'un
  // rendu sur un POST qu'on vient de faire, ce latch évite de le rejouer. Il ne
  // sert qu'à dédupliquer une valeur identique, jamais à bloquer un changement.
  let lastConfirmedAttr = null;

  /**
   * Maintient l'attribut panier `_ff_sale` aligné sur le contenu : posé (valeur
   * = tag de la vente) dès que le panier contient du F&F, vidé dès qu'il n'en
   * contient plus — pour ne jamais marquer à tort une commande normale passée
   * ensuite avec le même panier. Résout toujours (les erreurs sont loguées) :
   * le marquage ne doit bloquer ni un ajout ni un checkout.
   */
  function syncOrderAttribute(state) {
    if (!state) return Promise.resolve();

    const desired = state.ff.length > 0 ? state.tag : '';
    if (desired === state.attr || desired === lastConfirmedAttr) {
      lastConfirmedAttr = desired;
      return Promise.resolve();
    }
    if (attrSyncInFlight) return Promise.resolve();
    attrSyncInFlight = true;

    const attributes = {};
    attributes[ORDER_ATTRIBUTE] = desired;

    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes: attributes }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`cart/update.js a répondu ${response.status}`);
        lastConfirmedAttr = desired;
      })
      .catch((error) => {
        console.error("ff-cart-guard : pose de l'attribut de commande impossible", error);
      })
      .finally(() => {
        attrSyncInFlight = false;
      });
  }

  /* --------------------------------------------------- réconciliation

     Un panier mixte ne devrait pas exister, mais peut apparaître malgré le
     blocage à l'ajout : course entre deux onglets, panier antérieur à
     l'activation de la vente, permalien /cart/{variant}:1. Plutôt que de
     bloquer quantités et checkout cas par cas, on répare l'invariant dès
     qu'on le constate : le F&F gagne, le reste part. */

  function reconcile() {
    const state = readState();
    if (!isMixed(state)) return;

    purgeStandardLines(state)
      .then((purged) => {
        if (purged) showDrawerNotice(state.purgedMessage);
      })
      .catch((error) => {
        console.error('ff-cart-guard : réconciliation du panier impossible', error);
      });
  }

  /* -------------------------------------------------------- garde à l'ajout */

  /**
   * Purge puis ré-soumission du formulaire. On ne refait pas l'ajout à la main :
   * product-form.js s'en charge, ce qui préserve properties[_sur_type], le
   * tracking et le re-render des sections. Le re-render de l'ajout reflète aussi
   * la purge, il n'y a donc rien à rafraîchir entre les deux.
   */
  function purgeThenAdd(form, state) {
    setLoading(form, true);

    removeLines(state.std)
      .then(() => {
        noticeOnNextUpdate(state.purgedMessage);
        form.dataset[BYPASS_FLAG] = 'true';

        const submitButton = form.querySelector('[type="submit"]');
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitButton);
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      })
      .catch((error) => {
        console.error('ff-cart-guard : purge du panier impossible', error);
        setLoading(form, false);
      });
  }

  function guardAddToCart(event, form) {
    // Ré-soumission déclenchée par purgeThenAdd() : on laisse passer.
    if (form.dataset[BYPASS_FLAG] === 'true') {
      delete form.dataset[BYPASS_FLAG];
      return;
    }

    const state = readState();
    if (!state) return;

    const productIsFF = isFFProduct(form);

    // Panier F&F + produit hors F&F : refus.
    if (state.ff.length > 0 && !productIsFF) {
      event.preventDefault();
      event.stopPropagation();
      showFormMessage(form, state.blockedMessage);
      return;
    }

    // Produit F&F + articles hors F&F au panier : purge puis ajout.
    if (productIsFF && state.std.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      purgeThenAdd(form, state);
    }
  }

  /* ------------------------------------------------------ garde au checkout */

  function submitCheckout(form, submitter) {
    form.dataset[BYPASS_FLAG] = 'true';

    if (typeof form.requestSubmit === 'function' && submitter && submitter.isConnected) {
      form.requestSubmit(submitter);
    } else {
      // form.submit() ne transmet pas le name du bouton : on pose le paramètre
      // checkout nous-mêmes, sinon le POST /cart resterait sur le panier.
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      form.appendChild(hidden);
      form.submit();
    }
  }

  /**
   * L'état DOM de cet onglet peut être périmé si le panier a été modifié depuis
   * un autre onglet : on revalide auprès du serveur avant de laisser partir au
   * checkout. Panier mixte => réconciliation et on reste sur le panier ; envoyer
   * le client payer un panier qu'on vient d'amputer serait pire que ce détour.
   * En cas de pépin réseau sur la revalidation, on laisse partir : ne jamais
   * bloquer une vente sur un échec technique de notre propre garde-fou.
   */
  function guardCheckout(event, form, submitter) {
    if (form.dataset[BYPASS_FLAG] === 'true') {
      delete form.dataset[BYPASS_FLAG];
      return;
    }

    // Mécanique inactive (settings.ff_tag vide) : rien à valider.
    if (!document.getElementById(STATE_ELEMENT_ID)) return;

    // Revalidation déjà en cours (double clic) : on avale le second submit.
    if (submitter.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    submitter.setAttribute('aria-disabled', 'true');
    submitter.classList.add('loading');

    fetchFreshState()
      .then((state) => {
        if (isMixed(state)) {
          return purgeStandardLines(state).then((purged) => {
            if (purged) showDrawerNotice(state.purgedMessage);
          });
        }
        // Dernier point de passage avant la commande : on s'assure que
        // l'attribut _ff_sale est posé (ou vidé) avant de laisser partir.
        return syncOrderAttribute(state).then(() => {
          submitCheckout(form, submitter);
        });
      })
      .catch(() => {
        submitCheckout(form, submitter);
      })
      .finally(() => {
        // Dans la branche purge, le re-render a remplacé le bouton : ces appels
        // sur l'ancien élément détaché sont sans effet, c'est voulu.
        submitter.classList.remove('loading');
        submitter.removeAttribute('aria-disabled');
      });
  }

  /* ------------------------------------------------------------- dispatch */

  function onSubmitCapture(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.dataset.type === 'add-to-cart-form') {
      guardAddToCart(event, form);
      return;
    }

    // event.submitter peut manquer sur de vieux navigateurs : on retombe sur
    // l'élément actif, et à défaut on laisse passer (le gate est un filet, pas
    // une barrière — la réconciliation couvre déjà le panier lui-même).
    const submitter =
      event.submitter ||
      (document.activeElement && document.activeElement.name === 'checkout' ? document.activeElement : null);
    if (submitter && submitter.name === 'checkout') {
      guardCheckout(event, form, submitter);
    }
  }

  document.addEventListener('submit', onSubmitCapture, true);

  // Réconciliation + marquage : au chargement (panier mixte préexistant,
  // attribut manquant ou obsolète)...
  reconcile();
  syncOrderAttribute(readState());

  // ...après chaque mise à jour du panier (le paint de product-form.js suit son
  // publish : setTimeout pour relire l'état une fois le markup à jour)...
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      setTimeout(() => {
        reconcile();
        syncOrderAttribute(readState());
      });
    });
  }

  // ...et au retour sur l'onglet, avec resynchronisation depuis le serveur :
  // c'est ce qui rattrape la course entre deux onglets sans attendre une action.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!document.getElementById(STATE_ELEMENT_ID)) return;

    fetchFreshState()
      .then((state) => {
        reconcile();
        return syncOrderAttribute(state);
      })
      .catch(() => {});
  });
})();
